import React from "react";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { MEASUREMENT_LINES, PROTOCOL_NOTE } from "./editorColors";
import { formatIndex, formatMm, formatPx } from "../../lib/formatMeasurement";
import { classifyMeasurements } from "../../lib/cranialClassification";
import CranialSeverityChip from "../../../../appointment/components/cranialAsymmetry/CranialSeverityChip";
import styles from "./CranialEditor.module.css";

export const MEASUREMENT_ERRORS = {
  CONTOUR_TOO_SHORT: "Contorno insuficiente. Revise os pontos do contorno.",
  NO_AXIS_INTERSECTION: "O eixo não cruza o contorno. Ajuste os marcos N (nariz), O (orelhas) e P, ou use P automático.",
  NO_WIDTH: "Não foi possível medir a largura. Separe os marcos O das duas orelhas.",
  NO_DIAGONAL: "Não foi possível traçar as diagonais. Revise o contorno e os marcos.",
};

const DEFAULT_ERROR = "Não foi possível calcular as medidas. Revise o contorno e os marcos.";

function ValueCell({ px, mm }) {
  return (
    <span className={styles.measureValue}>
      {mm !== undefined && mm !== null ? (
        <>
          <strong>{formatMm(mm)}</strong>
          <span className={styles.measureSecondary}>{formatPx(px)}</span>
        </>
      ) : (
        <strong>{formatPx(px)}</strong>
      )}
    </span>
  );
}

/** Live measurement values (px always, mm when calibrated) with the overlay legend. */
function MeasurementPanel({ measurements }) {
  if (!measurements) {
    return (
      <section className={styles.panel}>
        <Typography className={styles.panelTitle}>Medidas</Typography>
        <Typography variant="body2" color="text.secondary">
          Ajuste o contorno e os marcos para calcular as medidas.
        </Typography>
      </section>
    );
  }

  if (!measurements.ok) {
    return (
      <section className={styles.panel}>
        <Typography className={styles.panelTitle}>Medidas</Typography>
        <Alert severity="warning" className={styles.panelAlert}>
          {MEASUREMENT_ERRORS[measurements.error] || DEFAULT_ERROR}
        </Alert>
      </section>
    );
  }

  const { px, mm, indices, formula, protocol, derived } = measurements;
  const classification = classifyMeasurements(measurements);

  return (
    <section className={styles.panel}>
      <Typography className={styles.panelTitle}>Medidas</Typography>
      <ul className={styles.measureList}>
        {MEASUREMENT_LINES.map((def) => (
          <li key={def.key} className={styles.measureRow}>
            <span className={styles.measureLabel}>
              <span className={styles.swatch} style={{ backgroundColor: def.color }} aria-hidden="true" />
              {def.label}
            </span>
            <ValueCell px={px[def.key]} mm={mm ? mm[def.key] : null} />
          </li>
        ))}
        <li className={styles.measureRow}>
          <span className={styles.measureLabel}>CVA</span>
          <ValueCell px={px.cva} mm={mm ? mm.cva : null} />
        </li>
        <li className={`${styles.measureRow} ${styles.measureRowStrong}`}>
          <span className={styles.measureLabel}>CVAI</span>
          {classification ? (
            <CranialSeverityChip
              compact
              value={formatIndex(indices.cvai)}
              label={classification.cvai.label}
              color={classification.cvai.color}
              textColor={classification.cvai.textColor}
            />
          ) : (
            <strong className={styles.measureValue}>{formatIndex(indices.cvai)}</strong>
          )}
        </li>
        <li className={`${styles.measureRow} ${styles.measureRowStrong}`}>
          <span className={styles.measureLabel}>IC</span>
          {classification ? (
            <CranialSeverityChip
              compact
              value={formatIndex(indices.ci)}
              label={classification.ci.label}
              color={classification.ci.color}
              textColor={classification.ci.textColor}
            />
          ) : (
            <strong className={styles.measureValue}>{formatIndex(indices.ci)}</strong>
          )}
        </li>
      </ul>
      {!mm ? (
        <Typography variant="caption" color="text.secondary" component="p">
          Sem escala: medidas lineares em pixels.
        </Typography>
      ) : null}
      {derived && derived.posteriorSource === "manual" ? (
        <Typography variant="caption" color="text.secondary" component="p">
          P ajustado manualmente.
        </Typography>
      ) : null}
      {formula ? (
        <Typography variant="caption" className={styles.formulaNote} component="p">
          {protocol ? `${PROTOCOL_NOTE} ` : ""}
          Fórmula {formula.name} v{formula.version}.
        </Typography>
      ) : null}
    </section>
  );
}

export default MeasurementPanel;
